import React, { useEffect, useMemo, useState } from "react";
import TitleButtons from "../../../components/TitleButtons";
import TextField from "../../../components/TextField";
import TextFieldRow from "../../../components/TextFieldRow";
import Button from "../../../components/Button";

const API_URL =
  "https://api.eva-googlemarine.com/departamentos/coordenacao/empresa-clientes";

const API_COLABS =
  "https://api.eva-googlemarine.com/departamentos/recursos-humanos/colaboradores-coordenacao";

const onlyDigits = (s: unknown) => String(s ?? "").replace(/\D+/g, "");

function maskCNPJ(s: string): string {
  const d = onlyDigits(s).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return d.replace(/^(\d{2})(\d+)/, "$1.$2");
  if (d.length <= 8) return d.replace(/^(\d{2})(\d{3})(\d+)/, "$1.$2.$3");
  if (d.length <= 12)
    return d.replace(/^(\d{2})(\d{3})(\d{3})(\d+)/, "$1.$2.$3/$4");
  return d.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2}).*/,
    "$1.$2.$3/$4-$5"
  );
}

const NoticeBanner: React.FC<{
  notice: { type: "success" | "error" | "notify"; text: string };
  onClose?: () => void;
  sticky?: boolean;
}> = ({ notice, onClose, sticky = true }) => {
  let bg, color, border;
  switch (notice.type) {
    case "success":
      bg = "#e6ffed";
      color = "#0f5132";
      border = "#badbcc";
      break;
    case "error":
      bg = "#ffecec";
      color = "#842029";
      border = "#f5c2c7";
      break;
    default:
      bg = "#e7f1ff";
      color = "#084298";
      border = "#b6d4fe";
      break;
  }

  return (
    <div
      role="alert"
      style={{
        margin: "12px 0",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border: `1px solid ${border}`,
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar aviso"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

type ColabApiItem = {
  id: string | number;
  nome?: string | null;
  nome_completo?: string | null;
};

type ColabOpt = {
  id: number;
  label: string; // ex: "JOÃO SILVA (12)"
};

function asInt(v: unknown): number | null {
  const n = Number(String(v ?? "").trim());
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return i > 0 ? i : null;
}

const RegistrarEmpresa: React.FC = () => {
  // ===== obrigatórios =====
  const [razao, setRazao] = useState("");
  const [fantasia, setFantasia] = useState("");
  const [cnpjView, setCnpjView] = useState("");

  // ===== coordenador responsável (dropdown) =====
  const [coordenadorLabel, setCoordenadorLabel] = useState(""); // o texto mostrado no input
  const [coordenadorId, setCoordenadorId] = useState<number | null>(null); // o que vai pro backend

  const [colabs, setColabs] = useState<ColabOpt[]>([]);
  const [loadingColabs, setLoadingColabs] = useState(false);
  const [erroColabs, setErroColabs] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<null | {
    type: "success" | "error" | "notify";
    text: string;
  }>(null);

  const onChangeCNPJ = (v: string) => setCnpjView(maskCNPJ(v));
  const cnpjDigits = onlyDigits(cnpjView);

  const colabDropdown = useMemo(
    () => (colabs.length ? colabs.map((c) => c.label) : undefined),
    [colabs]
  );

  // sempre que o usuário selecionar/digitar um label, tenta resolver o ID
  useEffect(() => {
    const found = colabs.find((c) => c.label === coordenadorLabel);
    setCoordenadorId(found?.id ?? null);
  }, [coordenadorLabel, colabs]);

  // 🔽 carrega colaboradores coordenação (para dropdown)
  useEffect(() => {
    let cancelled = false;

    async function loadColabs() {
      setLoadingColabs(true);
      setErroColabs(null);

      try {
        const res = await fetch(API_COLABS, {
          method: "GET",
          headers: { Accept: "application/json" },
          credentials: "include",
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok || !data?.ok || !Array.isArray(data?.data)) {
          const msg =
            data?.erro ||
            data?.message ||
            `Falha ao carregar colaboradores (HTTP ${res.status}).`;
          throw new Error(msg);
        }

        const opts: ColabOpt[] = (data.data as ColabApiItem[])
          .map((c) => {
            const id = asInt(c.id);
            const nome = String(c.nome ?? c.nome_completo ?? "").trim();
            if (!id || !nome) return null;
            const label = `${nome.toUpperCase()} (${id})`;
            return { id, label };
          })
          .filter(Boolean) as ColabOpt[];

        opts.sort((a, b) => a.label.localeCompare(b.label));

        if (!cancelled) setColabs(opts);
      } catch (e: any) {
        if (!cancelled) {
          setColabs([]);
          setErroColabs(e?.message || "Falha ao carregar colaboradores.");
        }
      } finally {
        if (!cancelled) setLoadingColabs(false);
      }
    }

    loadColabs();
    return () => {
      cancelled = true;
    };
  }, []);

  function clearAll() {
    setRazao("");
    setFantasia("");
    setCnpjView("");
    setCoordenadorLabel("");
    setCoordenadorId(null);
  }

  const canSubmit = useMemo(() => {
    const obrigatoriosOk =
      razao.trim().length > 0 &&
      fantasia.trim().length > 0 &&
      cnpjDigits.length === 14;

    const coordenadorOk = coordenadorId != null;

    return obrigatoriosOk && coordenadorOk;
  }, [razao, fantasia, cnpjDigits, coordenadorId]);

  async function handleRegister() {
    setNotice(null);

    const erros: string[] = [];
    if (!razao.trim()) erros.push("Informe a Razão Social.");
    if (!fantasia.trim()) erros.push("Informe o Nome Fantasia.");
    if (!cnpjDigits || cnpjDigits.length !== 14) erros.push("CNPJ inválido.");
    if (!coordenadorId)
      erros.push("Selecione um Coordenador Responsável válido.");

    if (erros.length) {
      setNotice({ type: "error", text: erros.join(" ") });
      return;
    }

    // ✅ payload novo (somente o que você quer)
    const payload = {
      razao_social: razao.trim(),
      nome_fantasia: fantasia.trim(),
      cnpj: cnpjDigits,
      colaborador_interno_responsavel: coordenadorId, // ✅ ID (backend grava int[] {id})
    };

    setSubmitting(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        const msg =
          data?.erro ||
          (Array.isArray(data?.erros) && data.erros.join(" | ")) ||
          data?.message ||
          `Falha ao registrar empresa (HTTP ${res.status}).`;
        setNotice({ type: "error", text: msg });
        return;
      }

      setNotice({
        type: "success",
        text: `Empresa registrada! ID ${data?.data?.id ?? ""}.`,
      });
      clearAll();
    } catch (err: any) {
      setNotice({
        type: "error",
        text: `Erro de rede: ${err?.message || "tente novamente."}`,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%" }}>
      <TitleButtons title="Departamento Pessoal - Registrar Empresa" />

      {(notice?.type === "success" || notice?.type === "notify") && (
        <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
      )}

      {erroColabs && (
        <NoticeBanner
          notice={{
            type: "notify",
            text: `Aviso: não foi possível carregar colaboradores. ${erroColabs}`,
          }}
          onClose={() => setErroColabs(null)}
        />
      )}

      <div style={{ display: "grid", gap: 10, width: "100%" }}>
        <div style={{ width: "100%" }}>
          <TextField
            label="Razão Social *"
            value={razao}
            onChange={setRazao}
            placeholder="Ex.: Marinetec Equipamentos e Serviços LTDA"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="Nome Fantasia *"
            value={fantasia}
            onChange={setFantasia}
            placeholder="Ex.: Marinetec"
            uppercase
            style={{ width: "100%" }}
          />
        </div>

        <div style={{ width: "100%" }}>
          <TextField
            label="CNPJ *"
            value={cnpjView}
            onChange={onChangeCNPJ}
            placeholder="00.000.000/0000-00"
            style={{ width: "100%" }}
          />
        </div>

        <TextFieldRow
          center={{
            label: "Coordenador Responsável *",
            value: coordenadorLabel,
            onChange: setCoordenadorLabel,
            placeholder: loadingColabs
              ? "CARREGANDO COLABORADORES..."
              : colabs.length
              ? "SELECIONE O COORDENADOR"
              : "SEM COLABORADORES DISPONÍVEIS",
            options: colabDropdown,
          }}
        />

        {/* feedback rápido quando digitou algo que não bate com a lista */}
        {!loadingColabs && coordenadorLabel.trim() && !coordenadorId && (
          <div style={{ fontSize: 12, color: "#842029" }}>
            Selecione um nome da lista para vincular o ID do colaborador.
          </div>
        )}
      </div>

      {notice?.type === "error" && (
        <NoticeBanner
          notice={notice}
          onClose={() => setNotice(null)}
          sticky={false}
        />
      )}

      <div style={{ marginTop: 12 }}>
        <Button
          text={submitting ? "Registrando..." : "Registrar Empresa"}
          onClick={handleRegister}
          disabled={submitting || !canSubmit}
        />
      </div>
    </div>
  );
};

export default RegistrarEmpresa;
